import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import FolderTabs from '../../components/common/FolderTabs';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ProductCard from '../../components/products/ProductCard';
import ProductEditorDialog from '../../components/products/ProductEditorDialog';
import ProductSalesPanel from '../../components/products/ProductSalesPanel';
import ProductCommissionSettingsCard from '../../components/products/ProductCommissionSettingsCard';
import RecordProductSaleDialog from '../../components/products/RecordProductSaleDialog';
import RecordStockPurchaseDialog from '../../components/stock/RecordStockPurchaseDialog';
import StockSection from '../stock/StockPage';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ProductService from '../../services/productService';
import ProductSaleService from '../../services/productSaleService';
import StockService from '../../services/stockService';
import SupplierService from '../../services/supplierService';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { describeApiError } from '../../utils/stockErrorUtils';
import { applyProductSaleSave } from '../../utils/productSaleSave';

const CLOSED_DIALOG_STATE = { open: false, mode: 'create', product: null };
const STOCK_TABS = ['stock', 'purchases', 'movements', 'adjustments', 'suppliers'];

const resolveActiveTab = (tabParam, { canReadProducts, canReadStock, canSeeCosts, canAdjust }) => {
  const requested = tabParam === 'settings' ? 'commission' : tabParam === 'overview' ? 'stock' : tabParam;

  if (requested === 'sales' || requested === 'commission') {
    return canReadProducts ? requested : (canReadStock ? 'stock' : 'products');
  }

  if (STOCK_TABS.includes(requested) && canReadStock) {
    if (requested === 'purchases' && !canSeeCosts) return 'stock';
    if (requested === 'adjustments' && !canAdjust) return 'stock';
    return requested;
  }

  if (!canReadProducts && canReadStock) {
    return 'stock';
  }

  return 'products';
};

const sortTeamMembersByName = (teamMembers) => (
  [...(teamMembers || [])].sort((left, right) => (
    String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base' })
  ))
);

const ProductsPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useToast();
  const { managementUser } = useAuth();
  const { hasPermission } = usePermissions(storeId);
  const canReadProducts = hasPermission(Permissions.ProductsRead);
  const canReadStock = hasPermission(Permissions.StockRead);
  const canSeeCosts = hasPermission(Permissions.StockCostsRead);
  const canAdjustStock = hasPermission(Permissions.StockAdjust);
  const activeTab = resolveActiveTab(searchParams.get('tab'), {
    canReadProducts,
    canReadStock,
    canSeeCosts,
    canAdjust: canAdjustStock,
  });

  const [products, setProducts] = useState([]);
  const [productContext, setProductContext] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG_STATE);
  const [saleDialog, setSaleDialog] = useState({ open: false, initialProductId: null });
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, initialProductId: null });
  const [suppliers, setSuppliers] = useState([]);
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [salesRefreshKey, setSalesRefreshKey] = useState(0);
  const [productToDelete, setProductToDelete] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingSale, setSavingSale] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);

  const canCreateProducts = hasPermission(Permissions.ProductsCreate);
  const canEditProducts = hasPermission(Permissions.ProductsUpdate);
  const canDeleteProducts = hasPermission(Permissions.ProductsDelete);
  const canCreatePurchase = hasPermission(Permissions.StockPurchasesCreate);
  const canCreateSales = hasPermission(Permissions.ProductSalesCreate);
  const canAssignSeller = hasPermission(Permissions.ProductSalesAssignSeller);
  const canVoidSales = hasPermission(Permissions.ProductSalesVoid);
  const canUpdateSettings = hasPermission(Permissions.ProductsSettingsUpdate);
  const canManageProducts = canCreateProducts || canEditProducts || canDeleteProducts;

  const tabs = useMemo(() => ([
    ...(canReadProducts ? [
      { value: 'products', label: t('productsManagement.tabs.products') },
      { value: 'sales', label: t('productsManagement.tabs.sales') },
      { value: 'commission', label: t('productsManagement.tabs.commission') },
    ] : []),
    ...(canReadStock ? [
      { value: 'stock', label: t('stock.tabs.stock') },
      ...(canSeeCosts ? [{ value: 'purchases', label: t('stock.tabs.purchases') }] : []),
      { value: 'movements', label: t('stock.tabs.movements') },
      ...(canAdjustStock ? [{ value: 'adjustments', label: t('stock.tabs.adjustments') }] : []),
      { value: 'suppliers', label: t('stock.tabs.suppliers') },
    ] : []),
  ]), [canAdjustStock, canReadProducts, canReadStock, canSeeCosts, t]);

  const loadProducts = useCallback(async () => {
    if (!canReadProducts) {
      setLoading(false);
      return;
    }

    if (!storeId) {
      setError('productsManagement.error.noStoreSelected');
      setLoading(false);
      return;
    }

    const requestKey = `getProductManagement-${storeId}-${canCreatePurchase}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);

        const [data, teamMembersResult, supplierList] = await Promise.all([
          ProductService.getProductManagementData(storeId),
          TeamMemberService.getTeamMembersByStore(storeId).catch(() => []),
          canCreatePurchase
            ? SupplierService.getSuppliers(storeId).catch(() => [])
            : Promise.resolve([]),
        ]);
        setProductContext(data);
        setProducts(data.products);
        setTeamMembers(sortTeamMembersByName(teamMembersResult));
        setSuppliers(supplierList);
      } catch (loadError) {
        console.error('Failed to load products:', loadError);
        setError('productsManagement.error.loading');
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, canCreatePurchase, canReadProducts]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleTabChange = (nextTab) => {
    if (nextTab === 'products') {
      setSearchParams({});
      return;
    }

    setSearchParams({ tab: nextTab });
  };

  const isStockTab = STOCK_TABS.includes(activeTab);

  const applyImageChanges = async (productId, imageFile, removeImage) => {
    if (imageFile) {
      await ProductService.setProductImage(storeId, productId, imageFile);
      return;
    }

    if (removeImage) {
      await ProductService.deleteProductImage(storeId, productId);
    }
  };

  const handleSaveProduct = async (product) => {
    try {
      setSavingProduct(true);

      if (dialogState.mode === 'edit' && dialogState.product?.id) {
        await ProductService.updateProduct(storeId, dialogState.product.id, product);
        await applyImageChanges(dialogState.product.id, product.imageFile, product.removeImage);
        showSuccess(t('productsManagement.feedback.updated'));
      } else {
        const created = await ProductService.createProduct(storeId, product);
        await applyImageChanges(created.id, product.imageFile, false);
        showSuccess(t('productsManagement.feedback.created'));
      }

      setDialogState(CLOSED_DIALOG_STATE);
      await loadProducts();
    } catch (saveError) {
      console.error('Failed to save product:', saveError);
      showError(describeApiError(
        saveError,
        t,
        dialogState.mode === 'edit' ? 'productsManagement.error.update' : 'productsManagement.error.create',
      ));
    } finally {
      setSavingProduct(false);
    }
  };

  const handleImmediateImageSelect = async (file) => {
    if (!dialogState.product?.id) {
      return;
    }

    try {
      const url = await ProductService.setProductImage(storeId, dialogState.product.id, file);
      setDialogState((current) => ({
        ...current,
        product: { ...current.product, imageUrl: url },
      }));
      showSuccess(t('productsManagement.feedback.updated'));
      await loadProducts();
    } catch (error) {
      console.error('Failed to upload product image:', error);
      showError(t('productsManagement.error.image'));
    }
  };

  const handleImmediateImageRemove = async () => {
    if (!dialogState.product?.id) {
      return;
    }

    try {
      await ProductService.deleteProductImage(storeId, dialogState.product.id);
      setDialogState((current) => ({
        ...current,
        product: { ...current.product, imageUrl: null },
      }));
      showSuccess(t('productsManagement.feedback.updated'));
      await loadProducts();
    } catch (error) {
      console.error('Failed to delete product image:', error);
      showError(t('productsManagement.error.image'));
    }
  };

  const handleDeleteProduct = async () => {
    const target = productToDelete;
    if (!target) {
      return;
    }

    try {
      setDeletingProductId(target.id);
      await ProductService.deleteProduct(storeId, target.id);
      showSuccess(t('productsManagement.feedback.deleted'));
      await loadProducts();
    } catch (deleteError) {
      console.error('Failed to delete product:', deleteError);
      showError(t('productsManagement.error.delete'));
    } finally {
      setDeletingProductId(null);
      setProductToDelete(null);
    }
  };

  const openSaleDialog = (productId = null) => {
    setSaleDialog({ open: true, initialProductId: productId });
  };

  const openPurchaseDialog = (productId = null) => {
    setPurchaseDialog({ open: true, initialProductId: productId });
  };

  const handleSavePurchase = async (purchase) => {
    try {
      setSavingPurchase(true);
      await StockService.createPurchase(storeId, purchase);
      showSuccess(t('stock.purchases.saved'));
      setPurchaseDialog({ open: false, initialProductId: null });
      await loadProducts();
    } catch (error) {
      console.error('Failed to record purchase:', error);
      showError(describeApiError(error, t, 'stock.errors.purchase'));
    } finally {
      setSavingPurchase(false);
    }
  };

  const handleSaveSale = async (sale) => {
    setSavingSale(true);
    await applyProductSaleSave({
      save: (payload) => ProductSaleService.createProductSale(storeId, payload),
      sale,
      t,
      onRecorded: () => {
        showSuccess(t('productsManagement.feedback.saleRecorded'));
        setSaleDialog({ open: false, initialProductId: null });
        setSalesRefreshKey((current) => current + 1);
      },
      onError: (message) => {
        console.error('Failed to record product sale');
        showError(message);
      },
    });
    setSavingSale(false);
  };

  if (loading && !isStockTab) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('productsManagement.title')}
        pageSubtitle={t('productsManagement.subtitle')}
        variant="cards"
        withAction={canCreatePurchase || canManageProducts}
      />
    );
  }

  if (error && !isStockTab) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
        <Button variant="contained" onClick={loadProducts}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <ManagementPageLayout
      id="products-page"
      containerId="products-header"
      titleId="products-title"
      subtitleId="products-subtitle"
      pageTitle={t('productsManagement.title')}
      pageSubtitle={t('productsManagement.subtitle')}
      contentId="products-main-content"
    >
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        contentId="products-tabs-content"
        equalWidthOnMobile={tabs.length <= 4}
      >
        <Box role="tabpanel" hidden={activeTab !== 'products'} id="products-tabpanel-products">
          {activeTab === 'products' ? (
            <Box id="products-content" sx={{ p: 4 }}>
              {canCreatePurchase ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 4, pt: 1, flexWrap: 'wrap' }}>
                  <PrimaryActionButton
                    startIcon={<AddIcon />}
                    onClick={() => openPurchaseDialog()}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    {t('productsManagement.addNew')}
                  </PrimaryActionButton>
                </Box>
              ) : null}

              {products.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('productsManagement.noProducts')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('productsManagement.emptyHint')}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
                  {products.map((product) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                      <ProductCard
                        product={product}
                        currency={productContext?.currency}
                        loading={savingProduct || savingPurchase || deletingProductId === product.id}
                        canEdit={canEditProducts}
                        canDelete={canDeleteProducts}
                        canPurchase={canCreatePurchase}
                        onEdit={() => setDialogState({ open: true, mode: 'edit', product })}
                        onDelete={() => setProductToDelete(product)}
                        onPurchase={() => openPurchaseDialog(product.id)}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ) : null}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 'sales'} id="products-tabpanel-sales">
          {activeTab === 'sales' ? (
            <ProductSalesPanel
              storeId={storeId}
              teamMembers={teamMembers}
              currency={productContext?.currency}
              canVoid={canVoidSales}
              canCreateSales={canCreateSales}
              onRecordSale={() => openSaleDialog()}
              refreshKey={salesRefreshKey}
            />
          ) : null}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== 'commission'} id="products-tabpanel-commission">
          {activeTab === 'commission' ? (
            <Box sx={{ p: { xs: 2, md: 4 } }}>
              <ProductCommissionSettingsCard
                storeId={storeId}
                canEdit={canUpdateSettings}
                asTabPanel
                initialSettings={{
                  productCommissionPercent: productContext?.productCommissionPercent ?? 0,
                }}
              />
            </Box>
          ) : null}
        </Box>

        {isStockTab ? <StockSection activeTab={activeTab} /> : null}
      </FolderTabs>

      <RecordStockPurchaseDialog
        open={purchaseDialog.open}
        products={products}
        suppliers={suppliers}
        currency={productContext?.currency}
        canCreateProducts={canCreateProducts}
        initialProductId={purchaseDialog.initialProductId}
        saving={savingPurchase}
        onClose={() => setPurchaseDialog({ open: false, initialProductId: null })}
        onSave={handleSavePurchase}
      />

      <ProductEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        product={dialogState.product}
        supportedLanguages={productContext?.supportedLanguages || ['en']}
        currency={productContext?.currency}
        canSeeCosts={hasPermission(Permissions.StockCostsRead)}
        saving={savingProduct}
        onClose={() => setDialogState(CLOSED_DIALOG_STATE)}
        onSave={handleSaveProduct}
        onImmediateImageSelect={dialogState.mode === 'edit' ? handleImmediateImageSelect : undefined}
        onImmediateImageRemove={dialogState.mode === 'edit' ? handleImmediateImageRemove : undefined}
      />

      <RecordProductSaleDialog
        open={saleDialog.open}
        onClose={() => setSaleDialog({ open: false, initialProductId: null })}
        onSave={handleSaveSale}
        saving={savingSale}
        storeId={storeId}
        products={products}
        teamMembers={teamMembers}
        initialProductId={saleDialog.initialProductId}
        currencyCode={productContext?.currency}
        commissionPercent={productContext?.productCommissionPercent}
        currentTeamMemberId={managementUser?.id}
        canAssignSeller={canAssignSeller}
      />

      <ConfirmDialog
        open={Boolean(productToDelete)}
        onClose={() => setProductToDelete(null)}
        onConfirm={handleDeleteProduct}
        title={t('productsManagement.delete.title')}
        message={t('productsManagement.delete.message', {
          name: productToDelete
            ? Object.values(productToDelete.name || {}).find((value) => value?.trim()) || ''
            : '',
        })}
        confirmText={t('productsManagement.delete.confirm')}
        cancelText={t('productsManagement.delete.cancel')}
      />
    </ManagementPageLayout>
  );
};

export default ProductsPage;
